import { Box, Text, Stack, Flex, Circle, useColorModeValue } from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const CountdownTimer = ({ endDate, currentTime }) => {
    const { t } = useTranslation();
    const [timeRemaining, setTimeRemaining] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });
    const [isRunning, setIsRunning] = useState(true);

    // Référence pour stocker l'offset entre le temps serveur et le temps local
    const timeOffsetRef = useRef(0);
    // Référence pour le temps initial du serveur
    const initialServerTime = useRef(new Date(currentTime).getTime());
    // Référence pour le temps initial client
    const initialClientTime = useRef(Date.now());

    // Colors
    const bgColor = useColorModeValue('purple.50', 'purple.900');
    const borderColor = useColorModeValue('purple.200', 'purple.700');
    const textColor = useColorModeValue('purple.800', 'purple.100');
    const accentColor = useColorModeValue('purple.500', 'purple.300');

    useEffect(() => {
        // Calculer l'offset entre l'heure du serveur et l'heure locale
        timeOffsetRef.current = initialServerTime.current - initialClientTime.current;

        const calculateTimeRemaining = () => {
            // Calculer le temps actuel du serveur en ajoutant le temps écoulé depuis l'initialisation
            const elapsedSinceInit = Date.now() - initialClientTime.current;
            const currentServerTime = initialServerTime.current + elapsedSinceInit;

            const end = new Date(endDate).getTime();
            const timeDiff = end - currentServerTime;

            // Check if countdown has ended
            if (timeDiff <= 0) {
                setIsRunning(false);
                window.location.reload()
                return;
            }

            // Calculate remaining time components
            const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

            setTimeRemaining({ days, hours, minutes, seconds });
        };

        // Initial calculation
        calculateTimeRemaining();

        // Setup interval for countdown
        const interval = setInterval(calculateTimeRemaining, 1000);

        // Cleanup on unmount
        return () => clearInterval(interval);
    }, [endDate, currentTime]);

    // Composant statique pour jours, heures et minutes
    const StaticTimeUnit = ({ value, label }) => (
        <Stack spacing={1} align="center">
            <Circle
                size="45px"
                bg={bgColor}
                borderWidth="2px"
                borderColor={borderColor}
                boxShadow="md"
                position="relative"
                overflow="hidden"
            >
                <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color={accentColor}
                >
                    {value}
                </Text>
                <Circle
                    position="absolute"
                    bottom="0"
                    left="0"
                    right="0"
                    height="4px"
                    bg={accentColor}
                    opacity={0.3}
                />
            </Circle>
            <Text fontSize="xs" fontWeight="medium" color={textColor}>
                {label}
            </Text>
        </Stack>
    );

    const AnimatedSecondsUnit = ({ value, label }) => (
        <motion.div
            initial={{ scale: 1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3 }}
        >
            <Stack spacing={1} align="center">
                <Circle
                    size="45px"
                    bg={bgColor}
                    borderWidth="2px"
                    borderColor={borderColor}
                    boxShadow="md"
                    position="relative"
                    overflow="hidden"
                >
                    <motion.div
                        key={value}
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        <Text
                            fontSize="xl"
                            fontWeight="bold"
                            color={accentColor}
                        >
                            {value}
                        </Text>
                    </motion.div>
                    <Circle
                        position="absolute"
                        bottom="0"
                        left="0"
                        right="0"
                        height="4px"
                        bg={accentColor}
                        opacity={0.3}
                    />
                </Circle>
                <Text fontSize="xs" fontWeight="medium" color={textColor}>
                    {label}
                </Text>
            </Stack>
        </motion.div>
    );

    // Separator animation
    const Separator = () => (
        <Box alignSelf="center" mx={1} pb={6}>
            <motion.div
                animate={{
                    opacity: [1, 0.3, 1],
                }}
                transition={{
                    duration: 1,
                    repeat: Infinity,
                    repeatType: "loop"
                }}
            >
                <Text fontSize="xl" fontWeight="bold" color={accentColor}>:</Text>
            </motion.div>
        </Box>
    );

    return (
        <Box
            mt={6}
            borderWidth="1px"
            borderRadius="lg"
            p={4}
            bg={bgColor}
            borderColor={borderColor}
            boxShadow="sm"
        >
            <Stack spacing={2}>
                <Text fontWeight="medium" color={textColor} textAlign="center">
                    {isRunning ? t('Votre abonnement expire dans') : t('Abonnement expiré')}:
                </Text>

                <Flex justify="center" align="center" mt={2}>
                    <StaticTimeUnit value={timeRemaining.days} label={t('jours')} />
                    <Separator />
                    <StaticTimeUnit value={String(timeRemaining.hours).padStart(2, '0')} label={t('heures')} />
                    <Separator />
                    <StaticTimeUnit value={String(timeRemaining.minutes).padStart(2, '0')} label={t('min')} />
                    <Separator />
                    {isRunning
                        ?
                        <AnimatedSecondsUnit value={String(timeRemaining.seconds).padStart(2, '0')} label={t('sec')} />
                        :
                        <StaticTimeUnit value={String(timeRemaining.seconds).padStart(2, '0')} label={t('sec')} />}
                </Flex>

                <Text fontSize="xs" color="gray.500" textAlign="center" mt={2}>
                    {t('Date de fin')}: {new Date(endDate).toLocaleDateString()}
                </Text>
            </Stack>
        </Box>
    );
};

export default CountdownTimer;